import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { BadRequestResponse } from 'types/Errors'
import i18n from 'i18n'
import { isAxiosError } from 'shared/api/isAxiosError'
import { parseApiError } from 'shared/api/apiError'
import notifyServerError from 'shared/api/notifyServerError'
import putTransitOperationAudit from './api'
import DeclarationApi from '../apiConfig'
import { transitOperationCacheRoots } from '../../../../../features/declaration/transit-operation/keys'
import { NctsApiError } from '../useTransitOperationContext'
import { EditTransitOperationAuditRequest } from '../../../../../api/models/edit-transit-operation-audit-request'
import { extractErrorMessageFromBadRequestReason } from '../../../../../helpers/errors.helper'
import { ErrorResponse, parseAxiosTransitErrorResponse } from '../../../../../shared/api/parseAxiosTransitErrorResponse'

export default function useTransitOperationAudit() {
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const updateTransitOperationAudit = async (auditId: number, { status, reason }: EditTransitOperationAuditRequest) => {
    if (auditId === null) return

    try {
      await putTransitOperationAudit({
        id: auditId,
        data: {
          status,
          reason,
        },
      })
      queryClient.invalidateQueries({ queryKey: [DeclarationApi.paths.transitOperationAudit.queryKeys.rootPath] })
      transitOperationCacheRoots.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }))
      toast.success(t('audit.updated', { context: 'successful' }))
    } catch (error) {
      const apiError = parseApiError(error)
      if (apiError.status === 503) {
        toast.error(
          t('common.submit', { context: 'service_unavailable' }),
          {
            autoClose: false,
            closeOnClick: false,
            draggable: false,
          },
        )
      } else if (apiError.reason === 'serverError' && apiError.traceId) {
        notifyServerError(apiError.traceId)
      } else if (isAxiosError<ErrorResponse>(error)) {
        const {
          isConstraintViolation,
          constraintViolations,
          isNctsError,
          nctsErrors,
          generalMessage,
        } = parseAxiosTransitErrorResponse(error)

        if (isConstraintViolation && constraintViolations.length) {
          constraintViolations.forEach((errorItem: BadRequestResponse) => {
            const translatedString = extractErrorMessageFromBadRequestReason(errorItem)
            if (translatedString !== '' && translatedString !== null && translatedString !== undefined) {
              toast.error(`${t(`translations${i18n.language.toUpperCase()}:${errorItem.field}`)} ${translatedString}`)
            }
          })
        } else if (isNctsError || nctsErrors.length) {
          toast.error(t('messages.nctsValidationError', { count: nctsErrors.length }))
          nctsErrors.forEach((errorItem: NctsApiError, index: number) => {
            if (index < 10) {
              toast.error(`${errorItem.field} ${errorItem.description}`, { autoClose: (index + 1) * 2000 })
            }
          })
        } else if (error.response?.request.status === 413) {
          toast.error(
            t('common.submit', { context: 'payload_too_large' }),
            {
              autoClose: false,
              closeOnClick: false,
              draggable: false,
            },
          )
        } else {
          toast.error(generalMessage ?? t('common.submit', { context: 'error' }))
        }
      } else {
        toast.error(t('common.submit', { context: 'error' }))
      }
    }
  }

  return {
    updateTransitOperationAudit,
  }
}
